// catalog (catalog-outline.md "catalog"; platform target model §7, §8): one catalog per account with sparse location
// overrides. Products, categories, tax profiles and discount definitions (automatic with schedules, presets, comps) are
// multi-origin config (ADR-015): every command is served at the node and in the cloud, writes only the fields it was
// given, and records them as one `ConfigFieldsWritten` fact merged per field by HLC on every replica.
import { z } from 'zod';
import { CATALOG_COLOR_NAMES } from '@radish/core/catalog/color-name';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import {
  AgeRestrictionId, CategoryId, CourseId, DepartmentId, DiscountRuleId, EmployeeId, LocalDate, LocationId, ModifierGroupId, ModifierOptionId, Money, PrinterId, ProductId, Rate,
  TaxExemptionId, TaxProfileId, VariantId,
  WallClockTime,
} from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';
import { menuCommands, menuQueries } from './menu.ts';
import { voidReasonCommands, voidReasonQueries } from './void-reasons.ts';
import { tenderTypeCommands, tenderTypeQueries } from './tender-types.ts';
import { productImageCommands, productImageQueries } from './images.ts';
import { ModifierGroupFields, modifierCommands, modifierQueries, ProductModifierGroup, VariantFields, VariantView } from './modifiers.ts';
import { printerCommands, printerQueries } from './printers.ts';

// ---- shared shapes ---------------------------------------------------------------------------------------------------
/** Legacy `tax_profile`: how a product's price relates to sales tax. */
export const TaxTreatment = z.enum(['exclusive', 'inclusive', 'exempt']).meta({ id: 'TaxTreatment' });

const Name = z.string().trim().min(1).max(120);

/** Legacy `product_type`: `normal` (standard), `unit` (price per unit of weight), `custom` (open price typed at the till). */
/**
 * A catalog colour's name (legacy `products.color` / `categories.color`, `AppTypesColor`), stored beside its register
 * hex (ops plan 2026-10-23-catalog-colour-names, B1066): blue and light-blue, grey and gray share a hex, so the name is
 * what tells them apart. Core `catalogColorName` reads the pair; a write naming a colour stores that name's hex.
 */
export const CatalogColorName = z.enum(CATALOG_COLOR_NAMES).meta({ id: 'CatalogColorName' });

export const ItemKind = z.enum(['standard', 'unit', 'custom']).meta({ id: 'ItemKind' });

export const ProductFields = z
  .object({
    name: Name,
    posName: z.string().trim().max(60).nullable(),
    categoryId: CategoryId.nullable(),
    /** The catalog price: the card (listed) price under True Margin. */
    price: Money,
    taxTreatment: TaxTreatment,
    /** null = the location's default tax rate. */
    taxProfileId: TaxProfileId.nullable(),
    visibleOnPos: z.boolean(),
    visibleOnline: z.boolean(),
    archived: z.boolean(),
    /** On the register's Speed Screen (legacy `speed_screen`); absent = not on it (target model §7.1 on_speed_screen). */
    onSpeedScreen: z.boolean().optional(),
    /** Order on the till within a category and on the Speed Screen (fractional-index key, target model pos_sort_key). */
    posSortKey: z.string().min(1).max(64).optional(),
    // ---- lane D1: the rest of the legacy item (target model §7.1); every field optional, so older rows still decode.
    /** How the price is set (legacy `product_type`): a fixed price, a price per unit (weighed), or typed at the till. */
    itemKind: ItemKind.optional(),
    /** The unit a unit item is sold in ("oz", "lb"): the register's "Enter Number of Units" pad shows it. */
    unitLabel: z.string().trim().max(20).nullable().optional(),
    kitchenName: z.string().trim().max(120).nullable().optional(),
    receiptName: z.string().trim().max(120).nullable().optional(),
    onlineName: z.string().trim().max(120).nullable().optional(),
    description: z.string().max(350).nullable().optional(),
    departmentId: DepartmentId.nullable().optional(),
    courseId: CourseId.nullable().optional(),
    /** The ID check the register asks for before sending or paying (legacy `id_verification_id`). */
    ageRestrictionId: AgeRestrictionId.nullable().optional(),
    /** Categories the item is also listed in (legacy `display_category_ids`). */
    displayCategoryIds: z.array(CategoryId).max(50).optional(),
    speedScreenSortKey: z.string().min(1).max(64).optional(),
    /** Tax exempt overrides the product belongs to (legacy "Display tax exempt overrides"). */
    taxExemptionIds: z.array(TaxExemptionId).max(50).optional(),
    /** Modifier sets on the item, in card order (legacy `modifier_product` + overrides). */
    modifierGroups: z.array(ProductModifierGroup).max(50).optional(),
    /** false = 86'd, out of stock at this location (legacy `products.enabled`; `catalog.setItemAvailability`). */
    available: z.boolean().optional(),
    /** The register button's label colour (legacy `products.color`, a palette colour); absent = the register's brand colour (legacy getThemeColor). */
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
    /** The colour's name (legacy's stored value); absent or null = read from the hex (core `catalogColorName`). */
    colorName: CatalogColorName.nullable().optional(),
    /** Barcode / SKU of a product sold at one price (legacy `product_prices.sku` of its only price); variants carry their own. */
    sku: z.string().trim().max(64).nullable().optional(),
    /** Order within its category in Pronto online ordering (legacy `online_store_ordering`, Display Order › Online Ordering Items). */
    onlineSortKey: z.string().min(1).max(64).optional(),
    // ---- lane D3: kitchen routing (printing.md R-P1): the item prints at its category's printers plus these, less those.
    /** Legacy `printer_product_include`. */
    includedPrinterIds: z.array(PrinterId).max(50).optional(),
    /** Legacy `printer_product_exclude`. */
    excludedPrinterIds: z.array(PrinterId).max(50).optional(),
  })
  .meta({ id: 'ProductFields' });

export const ProductView = ProductFields.extend({
  productId: ProductId,
  /** null = shared by every location of the account; otherwise the one location that owns it. */
  locationId: LocationId.nullable(),
  /** Fields this location overrides on a shared product. */
  overridden: z.array(z.string()),
  version: z.int().nonnegative(),
}).meta({ id: 'ProductView' });

export const CategoryFields = z
  .object({
    name: Name,
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    /** The colour's name (legacy's stored value); absent or null = read from the hex (core `catalogColorName`). */
    colorName: CatalogColorName.nullable().optional(),
    parentId: CategoryId.nullable(),
    visibleOnPos: z.boolean(),
    visibleOnline: z.boolean(),
    posSortKey: z.string().min(1).max(64),
    /**
     * The category's number on kitchen tickets (legacy `kitchen_receipt_ordering`, Display Order › Kitchen Ticket
     * Categories, numbered over the whole tree; equal numbers tie); absent = after every numbered one, in posSortKey order.
     */
    kitchenSortKey: z.string().min(1).max(64).optional(),
    /** Order in Pronto online ordering (legacy `online_store_ordering`); absent = posSortKey. */
    onlineSortKey: z.string().min(1).max(64).optional(),
    // ---- lane D3: kitchen routing; both optional, so older rows still decode.
    /** The category's default printers (legacy `category_printer`). */
    printerIds: z.array(PrinterId).max(50).optional(),
  })
  .meta({ id: 'CategoryFields' });

export const CategoryView = CategoryFields.extend({ categoryId: CategoryId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'CategoryView' });

/** A named sales-tax rate of one location (PTM §7.6); a product with no profile uses the location default. */
export const TaxProfileFields = z
  .object({ name: Name, rate: Rate, isLocationDefault: z.boolean() })
  .meta({ id: 'TaxProfileFields' });

export const TaxProfileView = TaxProfileFields.extend({ taxProfileId: TaxProfileId, locationId: LocationId, version: z.int().nonnegative() })
  .meta({ id: 'TaxProfileView' });

/** A fixed value, or an open one the register enters at the till (legacy preset with a blank amount). */
export const DiscountValue = z
  .discriminatedUnion('basis', [
    z.object({ basis: z.literal('percent'), rate: Rate }),
    z.object({ basis: z.literal('amount'), amount: Money }),
    z.object({ basis: z.literal('open_percent') }),
    z.object({ basis: z.literal('open_amount') }),
  ])
  .meta({ id: 'DiscountValue' });

export const DiscountTarget = z
  .object({
    targetKind: z.enum(['product', 'category']),
    targetId: z.uuid({ version: 'v7' }),
    effect: z.enum(['include', 'exclude']),
  })
  .meta({ id: 'DiscountTarget' });

/**
 * When an automatic discount applies, in the location's time zone. ISO weekdays (1 = Monday). An end before the start
 * crosses midnight and belongs to the business day it started in (Happy Hour 14:00–02:00).
 */
export const DiscountSchedule = z
  .object({
    startsOn: LocalDate.nullable(),
    endsOn: LocalDate.nullable(),
    weekdays: z.array(z.int().min(1).max(7)).max(7),
    startLocal: WallClockTime.nullable(),
    endLocal: WallClockTime.nullable(),
  })
  .meta({ id: 'DiscountSchedule' });

export const DiscountRuleFields = z
  .object({
    name: Name,
    published: z.boolean(),
    /**
     * `automatic`: the register applies it by schedule and targets. `preset`: staff apply it by hand. `price_adjustment`:
     * a scheduled markup, markdown or fixed price the register puts on matching items as it rings them (legacy Price
     * Adjustments, W-29).
     */
    application: z.enum(['automatic', 'preset', 'price_adjustment']),
    /**
     * A comp gives the item away (reported as comps, not discounts); legacy "Comp Drink" is a 100 % comp preset. A price
     * adjustment is a `price_markup`, `price_markdown` or `price_fixed` (the item sells at the value).
     */
    adjustmentKind: z.enum(['discount', 'comp', 'price_markup', 'price_markdown', 'price_fixed']),
    discountScope: z.enum(['item', 'order']),
    value: DiscountValue,
    targets: z.array(DiscountTarget).max(500),
    schedules: z.array(DiscountSchedule).max(20),
    combinability: z.enum(['stackable', 'exclusive', 'best_of']),
  })
  .meta({ id: 'DiscountRuleFields' });

export const DiscountRuleView = DiscountRuleFields.extend({
  discountRuleId: DiscountRuleId, locationId: LocationId.nullable(), version: z.int().nonnegative(),
}).meta({ id: 'DiscountRuleView' });

// ---- errors ----------------------------------------------------------------------------------------------------------
export { CatalogEntityNotFound, CatalogInvalid, ChangedSince, MenuItemRefused } from './errors.ts';
import { CatalogEntityNotFound, CatalogInvalid, ChangedSince, MenuItemRefused } from './errors.ts';

const configErrors = [CatalogInvalid, CloudAuthoritative, ProposalRequired, CatalogEntityNotFound] as const;

// ---- commands --------------------------------------------------------------------------------------------------------
const common = {
  availability: 'location',
  origin: 'any',
  emits: [ConfigFieldsWritten],
  errors: configErrors,
  dryRun: true,
} as const;

export const UpsertProduct = defineCommand({
  ...common,
  name: 'catalog.upsertProduct',
  summary: 'Create a product or change some of its fields (price, tax treatment, visibility, category).',
  input: z.object({ productId: ProductId, locationId: LocationId, shared: z.boolean().optional(), changes: ProductFields.partial() }),
  output: ProductView,
  permission: 'items.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] },
  legacy: ['POST /v1/products', 'PUT /v1/products/{id}'],
});

/**
 * NEW-06 (ADR-015, decided G-B7 / DECIDE-BO-11): the back office's "Revert" on a shared product overridden at this
 * location. Every field the location overrides goes back to the account's value, at the node and in the cloud alike.
 */
export const RevertProductOverrides = defineCommand({
  ...common,
  name: 'catalog.revertProductOverrides',
  summary: "Revert a shared product's overrides at a location, so the location sells it with the account's values again.",
  input: z.object({ productId: ProductId, locationId: LocationId }),
  output: ProductView,
  permission: 'items.manage',
  risk: 'medium',
  agent: { expose: false },
});

export const DeleteProduct = defineCommand({
  ...common,
  name: 'catalog.deleteProduct',
  summary: 'Delete a product (a tombstone every replica applies).',
  input: z.object({ productId: ProductId, locationId: LocationId }),
  output: z.object({ productId: ProductId, deleted: z.literal(true) }),
  permission: 'items.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['staff.onboarding'], description: 'Only for an item added by mistake that never sold. An item that has sold is archived instead: catalog.upsertProduct with changes { archived: true }.' },
  legacy: ['DELETE /v1/products/{id}'],
});

export const UpsertCategory = defineCommand({
  ...common,
  name: 'catalog.upsertCategory',
  summary: 'Create a category or change some of its fields.',
  input: z.object({ categoryId: CategoryId, locationId: LocationId, shared: z.boolean().optional(), changes: CategoryFields.partial() }),
  output: CategoryView,
  permission: 'items.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] },
  legacy: ['POST /v1/categories', 'PUT /v1/categories/{id}'],
});

/**
 * How much one catalog.upsertMenu call takes: each kind's own cap, and at most UPSERT_MENU_MAX_STEPS rows in all. 990
 * items took about 27 s on a laptop (#1511), so a call stays near 10 s there and well inside the Broker's and Claude's
 * tool timeouts on staging hardware; a bigger menu is several calls (the menu-build skill splits it).
 */
export const UPSERT_MENU_MAX_STEPS = 400;
export const UPSERT_MENU_MAX_CATEGORIES = 200;
export const UPSERT_MENU_MAX_MODIFIER_GROUPS = 200;

/**
 * A whole menu in one call (coordinator, 2026-10-19, after Andrew's first Launchpad build made 82 single calls): its
 * categories, modifier groups (with their options), items (with the groups they offer, in `changes.modifierGroups`) and
 * sizes. Each is the same catalog.upsertCategory, catalog.upsertModifierGroup, catalog.upsertProduct or
 * catalog.upsertVariant, with its own permission, history and audit entry, all in ONE transaction, in that order, so an
 * item may name a category or a group the same call creates, and a size an item it creates. The menu changes at once, or
 * not at all.
 */
/**
 * The version a row was read at (ops plan 2026-10-19-launchpad-bulk-mode §4.6 item 2): when given, the call is refused
 * with catalog.changed_since if the row has moved on, so a whole-list field (a group's options, an item's groups) never
 * overwrites a change made since. 0 means the row must not exist yet. Left out, the row is written as before.
 */
const ExpectedVersion = z.int().nonnegative().optional();

export const UpsertMenu = defineCommand({
  name: 'catalog.upsertMenu',
  summary: "Create or change many of a location's categories, modifier groups, items and sizes in one call: all of them are written, or none.",
  input: z.object({
    locationId: LocationId,
    categories: z.array(z.object({ categoryId: CategoryId, changes: CategoryFields.partial(), expectedVersion: ExpectedVersion })).max(UPSERT_MENU_MAX_CATEGORIES).default([]),
    modifierGroups: z.array(z.object({ modifierGroupId: ModifierGroupId, changes: ModifierGroupFields.partial(), expectedVersion: ExpectedVersion })).max(UPSERT_MENU_MAX_MODIFIER_GROUPS).default([]),
    products: z.array(z.object({ productId: ProductId, changes: ProductFields.partial(), expectedVersion: ExpectedVersion })).max(UPSERT_MENU_MAX_STEPS).default([]),
    variants: z.array(z.object({ variantId: VariantId, changes: VariantFields.partial(), expectedVersion: ExpectedVersion })).max(UPSERT_MENU_MAX_STEPS).default([]),
  }).refine((i) => i.categories.length + i.modifierGroups.length + i.products.length + i.variants.length <= UPSERT_MENU_MAX_STEPS, {
    message: `at most ${String(UPSERT_MENU_MAX_STEPS)} rows in one call: send a bigger menu as several calls`, path: ['products'],
  }),
  output: z.object({
    /**
     * Each row as written, in the order written: its name, price (an item's or a size's), version (1 for one this call
     * created), and `archived`: an item archived, or a category hidden from the POS. A modifier group names its options; a
     * size names its item.
     */
    records: z.array(z.object({
      kind: z.enum(['category', 'modifierGroup', 'product', 'variant']), id: z.uuid(), name: z.string(), price: Money.nullable(), version: z.int().positive(), archived: z.boolean(),
      optionNames: z.array(z.string()).optional(),
      productId: z.uuid().optional(),
      productName: z.string().optional(),
    })),
  }),
  permission: 'items.manage',
  risk: 'medium',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: "Launchpad's Claude reaches a location only through the Radish Broker, in the cloud; each write syncs to the node as its own command.",
  emits: [],
  errors: [...configErrors, MenuItemRefused, ChangedSince],
  dryRun: true,
  maxBodyBytes: 4 * 1024 * 1024,
  agent: {
    expose: true, surfaces: ['staff.onboarding'],
    description: `Prefer this to many single upserts: write the whole menu (or a whole change to it) in one call, at most ${String(UPSERT_MENU_MAX_STEPS)} rows; split a bigger menu into calls by category. Modifier groups carry their options (each with its own price); an item offers groups in changes.modifierGroups; a size is a variant with its own price, one of an item's sizes isDefault. A refusal names the row (details.at, name, reason) and nothing was written: fix it and send the whole call again. Archive an item with changes { archived: true }; hide a category with changes { visibleOnPos: false, visibleOnline: false }.`,
  },
});

export const DeleteCategory = defineCommand({
  ...common,
  name: 'catalog.deleteCategory',
  summary: 'Delete a category.',
  input: z.object({ categoryId: CategoryId, locationId: LocationId }),
  output: z.object({ categoryId: CategoryId, deleted: z.literal(true) }),
  permission: 'items.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['staff.onboarding'] },
  legacy: ['DELETE /v1/categories/{id}'],
});

export const UpsertTaxProfile = defineCommand({
  ...common,
  name: 'catalog.upsertTaxProfile',
  summary: "Create or change one of a location's sales-tax rates.",
  input: z.object({ taxProfileId: TaxProfileId, locationId: LocationId, changes: TaxProfileFields.partial() }),
  output: TaxProfileView,
  permission: 'settings.location.manage',
  risk: 'high',
  agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] },
  legacy: ['PATCH /v1/locations/{id}'],
});

export const UpsertDiscountRule = defineCommand({
  ...common,
  name: 'catalog.upsertDiscountRule',
  summary: 'Create or change a discount definition: automatic (scheduled), preset or comp.',
  input: z.object({ discountRuleId: DiscountRuleId, locationId: LocationId, shared: z.boolean().optional(), changes: DiscountRuleFields.partial() }),
  output: DiscountRuleView,
  permission: 'discounts.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/discounts', 'PUT /v1/discounts/{id}', 'POST /v1/preset-discounts', 'PUT /v1/preset-discounts/{id}'],
});

export const DeleteDiscountRule = defineCommand({
  ...common,
  name: 'catalog.deleteDiscountRule',
  summary: 'Delete a discount definition.',
  input: z.object({ discountRuleId: DiscountRuleId, locationId: LocationId }),
  output: z.object({ discountRuleId: DiscountRuleId, deleted: z.literal(true) }),
  permission: 'discounts.manage',
  risk: 'medium',
  agent: { expose: false },
  legacy: ['DELETE /v1/discounts/{id}', 'DELETE /v1/preset-discounts/{id}'],
});

// ---- the register's Edit Product form, through the store's node ------------------------------------------------------
// Plan 2026-10-11-parity-program, Decided 2026-10-12: names and prices are node fields, so the register writes them
// through its node on the store's network (they work with the internet down, as customer lookup does); only
// availability is the register's own offline fact (`catalog.setItemAvailability`). The register checks the acting
// employee's `item_management` (and that they are not restricted to availability) before it sends, as legacy did; the
// employee rides in the input for the audit trail.
const atRegister = { ...common, permission: { session: 'device' }, risk: 'medium', agent: { expose: false } } as const;

export const EditProductAtRegister = defineCommand({
  ...atRegister,
  name: 'catalog.editProductAtRegister',
  summary: "Save the register's Edit Product form: the product's names, its price, and the modifier prices it sets for itself.",
  input: z.object({
    productId: ProductId,
    locationId: LocationId,
    employeeId: EmployeeId,
    changes: z.object({
      name: ProductFields.shape.name.optional(),
      posName: ProductFields.shape.posName.optional(),
      kitchenName: ProductFields.shape.kitchenName,
      receiptName: ProductFields.shape.receiptName,
      /** A product sold at one price (no variant rows). */
      price: Money.optional(),
      /** Only prices the product already sets for itself (legacy `modifier_override_items`). */
      optionPrices: z.array(z.object({ modifierGroupId: ModifierGroupId, optionId: ModifierOptionId, price: Money })).max(200).optional(),
    }),
  }),
  output: ProductView,
  legacy: ['PUT /v1/products/{id} (register item management)'],
});

export const SetVariantPriceAtRegister = defineCommand({
  ...atRegister,
  name: 'catalog.setVariantPriceAtRegister',
  summary: "Save one variant's price from the register's Edit Product form.",
  input: z.object({ variantId: VariantId, locationId: LocationId, employeeId: EmployeeId, price: Money }),
  output: VariantView,
  legacy: ['PUT /v1/products/{id} (register item management, prices)'],
});

// ---- queries ---------------------------------------------------------------------------------------------------------
const byLocation = z.object({ locationId: LocationId });
const listQuery = { availability: 'location', scope: 'location', errors: [], agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] } } as const;

export const ListProducts = defineQuery({
  ...listQuery,
  name: 'catalog.products.list',
  summary: "The location's effective products: shared rows with this location's overrides, and its own rows.",
  input: byLocation,
  output: z.object({ products: z.array(ProductView) }),
  permission: { session: 'user' },
  legacy: ['GET /v1/products', 'GET /v1/products/till'],
});

export const ListCategories = defineQuery({
  ...listQuery,
  name: 'catalog.categories.list',
  summary: "The location's categories.",
  input: byLocation,
  output: z.object({ categories: z.array(CategoryView) }),
  permission: { session: 'user' },
  legacy: ['GET /v1/categories'],
});

export const ListTaxProfiles = defineQuery({
  ...listQuery,
  name: 'catalog.taxProfiles.list',
  summary: "The location's sales-tax rates.",
  input: byLocation,
  output: z.object({ taxProfiles: z.array(TaxProfileView) }),
  permission: { session: 'user' },
});

export const ListDiscountRules = defineQuery({
  ...listQuery,
  name: 'catalog.discountRules.list',
  summary: "The location's discount definitions: automatic, preset and comps.",
  input: byLocation,
  output: z.object({ discountRules: z.array(DiscountRuleView) }),
  permission: { session: 'user' },
  legacy: ['GET /v1/discounts/active', 'GET /v1/preset-discounts'],
});

export const catalogCommands = [
  UpsertProduct, RevertProductOverrides, DeleteProduct, EditProductAtRegister, SetVariantPriceAtRegister, UpsertCategory, UpsertMenu, DeleteCategory, UpsertTaxProfile, UpsertDiscountRule, DeleteDiscountRule, ...modifierCommands, ...menuCommands,
  ...voidReasonCommands,
  ...tenderTypeCommands, ...printerCommands, ...productImageCommands,
] as const;
export const catalogQueries = [ListProducts, ListCategories, ListTaxProfiles, ListDiscountRules, ...modifierQueries, ...menuQueries, ...voidReasonQueries, ...tenderTypeQueries, ...printerQueries, ...productImageQueries] as const;

export * from './modifiers.ts';
export * from './menu.ts';
export * from './void-reasons.ts';
export * from './printers.ts';
export * from './tender-types.ts';
export * from './images.ts';
